import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { QrCode } from 'lucide-react'
import type { ReactNode } from 'react'

import { useAuth } from '../lib/auth-context.ts'
import { money } from '../lib/inbox-commerce.ts'
import { exclusiveEnd, periodRange } from '../lib/period-range.ts'
import { fetchPixReport } from '../lib/pix-charge.ts'

/**
 * O Pix de hoje nesta filial, na tela do Caixa — F205.
 *
 * O caixa dá a baixa à mão; esta linha é o número para bater com o que ele
 * lançou. Só aparece para quem pode ver os números do Pix, e só se houve Pix.
 */
export const PixTodayLine = (): ReactNode => {
  const { allows, session } = useAuth()
  const branchId = session?.context?.branchId ?? undefined
  const canRead = allows('pix.charge.read')
  const today = periodRange('today')

  const report = useQuery({
    queryKey: ['pix-report', 'today', branchId ?? ''],
    queryFn: async ({ signal }) =>
      await fetchPixReport(
        {
          from: today.from,
          to: exclusiveEnd(today.to),
          ...(branchId === undefined ? {} : { branchId }),
          pageSize: 1,
        },
        signal
      ),
    enabled: canRead,
    refetchInterval: 60_000,
  })

  const data = report.data
  if (!canRead || data === undefined) return null
  if (data.paidCount === 0 && data.pendingCount === 0) return null

  return (
    <p className="flex flex-wrap items-center gap-2 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface) px-3 py-2 text-(length:--text-sm)">
      <QrCode aria-hidden="true" className="h-4 w-4 text-(--text-muted)" />
      <span>
        Pix pago hoje nesta filial:{' '}
        <strong className="font-(--font-mono) tabular-nums">
          {money(data.paidAmount)}
        </strong>{' '}
        ({String(data.paidCount)})
        {data.pendingCount > 0
          ? ` · ${String(data.pendingCount)} aguardando`
          : ''}
      </span>
      <Link to="/pix" className="ml-auto underline underline-offset-2">
        Ver os Pix
      </Link>
    </p>
  )
}
